import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Form, InputGroup, Table } from 'react-bootstrap';
import { connect } from 'react-redux';
import { Navigate } from 'react-router-dom';
import { setIsLoading } from '../general/actions';
import axiosInstance from '../general/axiosInstance';
import { Pager, apiError, formatDateTime } from '../payments/paymentUtils';

/**
 * Actividad de usuarios (solo superadmin; el backend responde 403 al resto).
 * "Conectado" = actividad en los últimos 5 minutos. "Última visita" = la más reciente entre la
 * última actividad de una sesión abierta y el último login.
 */
function Activity({ roles, setIsLoading }) {
    const allowed = (roles || []).includes('superadmin');

    const [data, setData] = useState(null);
    const [query, setQuery] = useState('');
    const [onlineOnly, setOnlineOnly] = useState(false);
    const [error, setError] = useState('');

    const load = useCallback(async (page = 1) => {
        setIsLoading(true);
        setError('');
        try {
            const res = await axiosInstance.get('/admin/activity', {
                params: { page, q: query.trim() || undefined, online: onlineOnly ? 1 : undefined },
            });
            setData(res.data);
        } catch (err) {
            setError(apiError(err, 'No se pudo cargar la actividad.'));
        } finally {
            setIsLoading(false);
        }
    }, [query, onlineOnly, setIsLoading]);

    useEffect(() => { if (allowed) load(1); }, [allowed, load]);

    if (!allowed) return <Navigate to="/main" replace />;

    return (
        <div style={{ marginTop: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
                <h5 style={{ fontWeight: 800, margin: 0 }}>
                    <i className="fa fa-clock-o" style={{ color: 'var(--abi-primary)', marginRight: 8 }} />
                    Actividad de usuarios
                </h5>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <Form.Check type="checkbox" id="online-only" label="Solo conectados" checked={onlineOnly}
                        onChange={(e) => setOnlineOnly(e.target.checked)} />
                    <InputGroup size="sm" style={{ width: 280 }}>
                        <Form.Control
                            placeholder="Buscar usuario, correo u organización"
                            maxLength={100}
                            defaultValue={query}
                            onKeyDown={(e) => { if (e.key === 'Enter') setQuery(e.target.value); }}
                            onBlur={(e) => { if (e.target.value !== query) setQuery(e.target.value); }}
                        />
                    </InputGroup>
                    <Button size="sm" variant="outline-primary" onClick={() => load(data?.current_page || 1)}>
                        <i className="fa fa-refresh" /> Refrescar
                    </Button>
                </div>
            </div>

            {error && <Alert variant="danger">{error}</Alert>}

            {data && data.data.length === 0 && !error && <Alert variant="info">No hay usuarios para este filtro.</Alert>}

            {data && data.data.length > 0 && (
                <>
                    <Table responsive className="report-table mb-0">
                        <thead>
                            <tr><th>Usuario</th><th>Organización</th><th>Roles</th><th>Estado</th><th>Última visita</th><th>Sesiones</th></tr>
                        </thead>
                        <tbody>
                            {data.data.map((u) => (
                                <tr key={u.id}>
                                    <td><strong style={{ fontSize: 13 }}>{u.name}</strong><div style={{ fontSize: 12, color: 'var(--abi-muted)' }}>{u.email}</div></td>
                                    <td style={{ fontSize: 12 }}>{u.organization?.name || '—'}</td>
                                    <td style={{ fontSize: 12 }}>{u.roles.length ? u.roles.join(', ') : '—'}</td>
                                    <td>
                                        {u.online ? (
                                            <span className="report-status-badge" style={{ background: '#dcfce7', color: '#166534', border: '1px solid #86efac' }}>Conectado</span>
                                        ) : (
                                            <span className="report-status-badge" style={{ background: '#f3f4f6', color: '#4b5563', border: '1px solid #d1d5db' }}>
                                                {u.open_sessions > 0 ? 'Inactivo' : 'Sin sesión'}
                                            </span>
                                        )}
                                    </td>
                                    <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{formatDateTime(u.last_visit)}</td>
                                    <td style={{ fontSize: 12 }}>{u.open_sessions || 0}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                    <Pager page={data} onChange={load} />
                </>
            )}
        </div>
    );
}

const mapStateToProps = (state) => ({ roles: state.roles });
export default connect(mapStateToProps, { setIsLoading })(Activity);
